import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { ArrowLeft, Plus, Trash2, X } from "lucide-react";
import { Spinner } from "reactstrap";
import accountApi from "../../api/accountApi";
import usersApi from "../../api/usersApi";
import taskApi from "../../api/taskApi";
import FormField from "../ui/FormField";
import { Button } from "../Button";
import { FaPaperclip, FaTrash, FaUpload } from "react-icons/fa";
import TaskUploadManager from "../Upload/TaskUploadManager";
import TaskDocumentsViewer from "../Upload/TaskDocumentsViewer";
import uploadApi from "../../api/uploadApi";
import { formatDateTime } from "../../utils/dateFormatter";
import { confirmSubmit } from "../../utils/confirmSubmit";
import { compressFile } from "../../utils/imageCompression";
import { useNavigate } from "react-router-dom";

const formatText = (text = "") =>
  text
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const AddTasksAll = ({
  taskId,
  onClose,
  onRefreshAccounts,
  setShowCreateTask,
  workflowDropdown,
  type,
  formData,
  setFormData,
  handleSubmit,
  isLoader,
  attachments,
  setAttachments,
  comments,
  setComments,
  errors,
}) => {
  const [isLoader2, setIsLoader2] = useState(false);
  const isEditMode = !!taskId;
  const token = useSelector((state) => state.auth.access_token);
  const user = useSelector((state) => state.auth.userdetails.fullName);
  const currentUserId = useSelector((state) => state.auth.userdetails.id);
  const [isAccountLocked, setIsAccountLocked] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [users, setUsers] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
const navigate = useNavigate();
  const handleUploadSuccess = () => {
    setShowUpload(false);
    setRefreshKey((prev) => prev + 1);
  };
  const handleFieldChange = (name) => (val) =>
    setFormData((prev) => ({ ...prev, [name]: val }));

  useEffect(() => {
    accountApi.getAllAccounts(token, 0, 10).then((res) => {
      const accList = res?.data?.content || [];
      setAccounts(accList);
    });

    usersApi.getAllUsers(token).then((res) => {
      setUsers(res?.data || []);
    });
  }, [token]);

 const handleFileUpload = async (e) => {
  const files = Array.from(e.target.files);
  if (!files.length) return;

  for (const originalFile of files) {
    setIsLoader2(true);

    try {
      const compressedFile = await compressFile(originalFile);

      if (compressedFile.size > 1 * 1024 * 1024) {
        toast.error(
          `${compressedFile.name} is too large. Max allowed size is 1MB.`
        );
        setIsLoader2(false);
        continue;
      }

      const fd = new FormData();
      fd.append("file", compressedFile);

      const cloudinaryRes = await uploadApi.uploadTask(
        taskId,
        "attachments",
        fd,
        token
      );

      const payload = {
        fileName: compressedFile.name,
        filePath: cloudinaryRes.fileUrl,
        contentType: compressedFile.type,
      };

      setAttachments((prev) => [...prev, payload]);

      if (taskId) {
        await taskApi.addAttachment(taskId, payload, token);
        toast.success("Attachment uploaded successfully");
        onRefreshAccounts();
      }
    } catch (err) {
      toast.error(`Failed to upload ${originalFile.name}`);
    } finally {
      setIsLoader2(false);
    }
  }

  e.target.value = "";
};

  const handleRemoveAttachment = async (index, attachment) => {
const isConfirmed = await confirmSubmit({
          title: "Confirm attachment delete",
          text: "Are you sure you want to delete this attachment?",
          confirmText: "Submit",
          icon: "error",
        });
    if (!isConfirmed) return;
    try {
      if (attachment?.id) {
        await taskApi.deleteAttachment(attachment.id, token);
        toast.success("Attachment deleted successfully");
        onRefreshAccounts();
      }

      setAttachments((prev) => prev.filter((_, i) => i !== index));
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete attachment");
    }
  };

  const handleFieldChangeWorkflow = (field) => (e) => {
    const value = e?.target?.value ?? e;

    if (field === "workflowId") {
      const selectedWorkflow = workflowDropdown.find(
        (w) => String(w.value) === String(value),
      );

      setFormData((prev) => ({
        ...prev,
        workflowId: value,
        workflow: selectedWorkflow?.label || "",
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAdd = () => {
    setComments([...comments, { content: "" }]);
  };

  const handleChange = (index, value) => {
    const updated = [...comments];
    updated[index].content = value;
    setComments(updated);
  };

  const handleRemove = async (index) => {
    const isConfirmed = await confirmSubmit({
              title: "Confirm comment delete",
              text: "Are you sure you want to delete this comment?",
              confirmText: "Submit",
            });

    if (!isConfirmed) return;
    const updated = comments.filter((_, i) => i !== index);
    setComments(updated);
  };

  const clearFormData = () => {
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);
    setFormData({
      title: "",
      description: "",
      priority: "",
      status: "TODO",
      workflow: "",
      account: "",
      assignedTo: "",
      createdBy: "",
      workflowId: 0,
      startDate: today.toISOString().split("T")[0],
      dueDate: tomorrow.toISOString().split("T")[0],
    });
    setAttachments([]);
    setComments([{ content: "" }]);
  };

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      className="bg-white rounded-xl shadow-md w-full p-3 overflow-y-auto"
    >
      <div className="flex flex-row items-center justify-start gap-2">
        <button
          onClick={() => {
            setShowCreateTask(taskId ? null : false);
            clearFormData();
          }}
          aria-label="Close"
          type="button"
          className="text-slate-800"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="text-lg font-semibold text-gray-800">
          {taskId ? "Edit Task" : "Create Task"}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
        {/* <div className="md:col-span-3"> */}
          <FormField
            label="Title"
            type="string"
            required
            maxLength={100}
            value={formData?.title}
            onChange={handleFieldChange("title")}
            error={errors?.title}
          />
        {/* </div> */}
        {type === "workflow" && (
          <FormField
            label="Workflow"
            type="dropdown"
            required
            value={formData?.workflowId}
            onChange={handleFieldChangeWorkflow("workflowId")}
            options={workflowDropdown}
            error={errors?.workflowId}
          />
        )}
        <FormField
          label="Assigned To"
          type="dropdown"
          required
          value={formData?.assignedTo}
          onChange={handleFieldChange("assignedTo")}
          options={users?.map((u) => ({
            label: u.fullName,
            value: u.id,
          }))}
          error={errors?.assignedTo}
        />

        <FormField
          label="Account"
          type="dropdown"
          required
          value={formData?.account}
          onChange={handleFieldChange("account")}
          disabled={isAccountLocked}
          options={accounts?.map((a) => ({
            label: a.accountName,
            value: a.id,
          }))}
          error={errors?.account}
        />

        {taskId ? (
          <FormField
            label="Status"
            type="dropdown"
            required
            value={formData?.status}
            error={errors?.status}
            onChange={handleFieldChange("status")}
            options={[
              "TODO",
              "IN_PROGRESS",
              "COMPLETED",
              "CLOSED",
              "PENDING",
              "IN_REVIEW",
              "PROPOSED",
              "OVERDUE",
            ]}
          />
        ) : (
          <></>
        )}

        <FormField
          label="Priority"
          type="dropdown"
          value={formData?.priority}
          error={errors?.priority}
          onChange={handleFieldChange("priority")}
          options={["LOW", "MEDIUM", "HIGH"]}
        />

        <FormField
          label="Start Date"
          type="date"
          required
          error={errors?.startDate}
          min={new Date().toISOString().split("T")[0]}
          value={formData?.startDate}
          onChange={handleFieldChange("startDate")}
        />

        <FormField
          label="Due Date"
          type="date"
          required
          min={formData?.startDate}
          error={errors?.dueDate}
          value={formData?.dueDate}
          onChange={handleFieldChange("dueDate")}
        />
      </div>

      <div className="mt-1">
        <FormField
          label="Description"
          type="description"
          error={errors?.description}
          rows="2"
          required
          value={formData?.description}
          onChange={handleFieldChange("description")}
        />
      </div>
      {/* {taskId && (
        <div className="mt-2">
          <button
            onClick={() => setShowUpload(true)}
            className="bg-primary text-white px-3 py-2 rounded-lg flex items-center gap-2"
          >
            <Plus size={16} />
            Upload Document
          </button>
        </div>
      )}
      <div className="py-2">
        <TaskDocumentsViewer taskId={taskId} refreshKey={refreshKey} />
      </div> */}

        <div className="border border-gray-200 rounded-lg p-2 mt-1">
        <div className="flex justify-between items-center mb-1">
          <div className="text-sm font-semibold text-gray-700">Attachments</div>
      
          <label className="flex items-center gap-1 cursor-pointer text-xs text-indigo-600 hover:text-indigo-800">
            <FaUpload size={12} />
            {isLoader2 ? <Spinner size={"sm"}/> : <span>Upload</span>}
            <input
              type="file"
              multiple
              className="hidden"
              onChange={handleFileUpload}
            />
          </label>
        </div>
      
        {attachments?.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {attachments.map((f, index) => (
              <div
                key={f.id}
                className="flex items-center justify-between p-2 border border-gray-100 rounded-md bg-white hover:bg-gray-100 transition"
              >
                <div className="flex flex-col min-w-0">
                 <div className="flex items-center gap-2">
                   <FaPaperclip className="text-gray-400 text-xs shrink-0" />
                   <a
                     href={f.filePath}
                     target="_blank"
                     rel="noopener noreferrer"
                     className="text-xs text-gray-700 truncate hover:underline"
                     title={f.fileName}
                   >
                     {f.fileName}
                   </a>
                 </div>
               
                 <span className="text-[10px] text-gray-400 ml-5">
                   {formatDateTime(f.uploadedAt)}
                 </span>
               </div>
               
      
                <button
                  onClick={() => handleRemoveAttachment(index, f)}
                  className="text-xs text-red-500 hover:text-red-700 shrink-0"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-gray-400">No attachments uploaded</p>
        )}
      </div>
      <div className="w-full space-y-3 my-2 p-2 border border-gray-200 rounded-lg">
        <div className="text-sm font-semibold mb-1">Comments</div>
        {comments?.map((item, index) => (
          <div key={index} className="flex gap-2 w-full">
            <input
              type="text"
              value={item.content}
              onChange={(e) => handleChange(index, e.target.value)}
              placeholder={`Comment ${index + 1}`}
              className="border rounded px-3 py-1 w-full"
            />

            {comments.length > 1 && (
              <button
                type="button"
                onClick={() => handleRemove(index)}
                className="text-red-500"
              >
                <Trash2 size={14} />
              </button>
            )}
            {index === comments.length - 1 && (
              <button
                type="button"
                onClick={handleAdd}
                className="bg-primary text-white px-2 rounded-full"
              >
                <Plus size={18} />
              </button>
            )}
          </div>
        ))}
      </div>
      <div className="flex flex-row items-center justify-end gap-2">
        <button
          type="button"
          onClick={() => {
            setShowCreateTask(taskId ? null : false);
            clearFormData();
          }}
          className="bg-red-50 py-2 px-4 border border-red-300 rounded-md text-sm font-medium text-red-700 hover:bg-red-100"
        >
          Cancel
        </button>
        {isLoader ? (
          <Button label={<Spinner size={"sm"} />} />
        ) : (
          <Button
            label={`${taskId ? "Update" : "Save"}`}
            onClick={handleSubmit}
          />
        )}
      </div>
      <TaskUploadManager
        taskId={taskId}
        isOpen={showUpload}
        onClose={() => setShowUpload(false)}
        onUploadSuccess={handleUploadSuccess}
      />
    
    </div>
  );
};

export default AddTasksAll;
